import { IEvaluateScript, ITransaction } from "@src/utils/types";
import makeNodeRequest from "@src/utils/makeNodeRequest";
import { CHAIN_ID } from "@src/constants";

export interface INodeData {
  key: string;
  type: "integer" | "string";
  value: number | string;
}

export interface IAsset {
  assetId: string;
  issueHeight: number;
  issueTimestamp: number;
  issuer: string;
  issuerPublicKey: string;
  name: string;
  description: string;
  decimals: number;
  reissuable: boolean;
  quantity: number;
  scripted: boolean;
  minSponsoredAssetFee: null | number;
  originTransactionId: string;
}

export interface INFT {
  assetId: string;
  decimals: number;
  description: string;
  issueHeight: number;
  issueTimestamp: number;
  issuer: string;
  issuerPublicKey: string;
  minSponsoredAssetFee: null | any;
  name: string;
  originTransactionId: string;
  quantity: number;
  reissuable: boolean;
  scripted: boolean;
  typeId?: string;
}

interface IBalance {
  assetId: string;
  balance: number;
}

interface IAssetDetails {
  assetId: string;
  issueHeight: number;
  issueTimestamp: number;
  issuer: string;
  issuerPublicKey: string;
  name: string;
  description: string;
  decimals: number;
  reissuable: boolean;
  quantity: number;
  scripted: boolean;
  minSponsoredAssetFee: null | any;
  originTransactionId: string;
}

interface ILeaseInfo {
  id: string;
  originTransactionId: string;
  sender: string;
  recipient: string;
  amount: number;
  height: number;
  status: string;
  cancelHeight: number;
  cancelTransactionId: string;
}

const nodeService = {
  blocksHeight: async (): Promise<{ height: number }> => {
    const url = `/blocks/height`;
    const { data } = await makeNodeRequest(url, {chainId: CHAIN_ID});
    return data;
  },
  getAddressNfts: async (address: string): Promise<INFT[]> => {
    const url = `/assets/nft/${address}/limit/1000`;
    const { data } = await makeNodeRequest(url, {chainId: CHAIN_ID});
    return data;
  },
  evaluate: async (
    address: string,
    expression: string
  ): Promise<IEvaluateScript> => {
    const url = `/utils/script/evaluate/${address}`;
    const { data } = await makeNodeRequest(url, {
      postData: { expr: expression },
      chainId: CHAIN_ID,
    });
    return data;
  },
  getAssetDetails: async (assetId: string): Promise<IAsset | null> => {
    const url = `/assets/details/${assetId}`;
    const { data } = await makeNodeRequest(url, {chainId: CHAIN_ID});
    return data;
  },
  getAddressBalances: async (address: string | null): Promise<IBalance[]> => {
    if (address == null) return [];
    const assetsUrl = `/assets/balance/${address}`;
    const wavesUrl = `/addresses/balance/details/${address}`;
    return (
      await Promise.all([
        makeNodeRequest(assetsUrl, {chainId: CHAIN_ID}).then(({ data }) => data),
        makeNodeRequest(wavesUrl, {chainId: CHAIN_ID}).then(({ data }) => ({
          balances: [{ balance: data.available, assetId: "WAVES" }],
        })),
      ])
    ).reduce<{ assetId: string; balance: number }[]>(
      (acc, { balances }) => [...acc, ...balances],
      []
    );
  },
  nodeKeysRequest: async (
    contract: string,
    keys: string[] | string
  ): Promise<INodeData[]> => {
    const searchKeys = typeof keys === "string" ? [keys] : keys;
    const search = new URLSearchParams(searchKeys?.map((s) => ["key", s]));
    const keysArray = search.toString();
    const response = await makeNodeRequest(
      `/addresses/data/${contract}?${keysArray}`, {chainId: CHAIN_ID}
    );
    if (response.data) {
      return response.data;
    } else {
      return [];
    }
  },
  nodeMatchRequest: async (
    contract: string,
    match: string
  ): Promise<INodeData[]> => {
    const url = `/addresses/data/${contract}?matches=${match}`;
    const response: { data: INodeData[] } = await makeNodeRequest(url, {chainId: CHAIN_ID});
    if (response.data) {
      return response.data;
    } else {
      return [];
    }
  },
  transactionInfo: async (txId: string): Promise<ITransaction | null> => {
    const url = `/transactions/info/${txId}`;
    const response: { data: ITransaction } = await makeNodeRequest(url, {chainId: CHAIN_ID});
    if (response.data) {
      return response.data;
    } else {
      return null;
    }
  },
  transactions: async (
    address: string,
    limit = 10,
    after?: string
  ): Promise<ITransaction[] | null> => {
    const urlSearchParams = new URLSearchParams();
    if (after != null) {
      urlSearchParams.set("after", after);
    }
    const url = `/transactions/address/${address}/limit/${limit}?${after != null ? urlSearchParams.toString() : ""
      }`;
    const response: { data: [ITransaction[]] } = await makeNodeRequest(url, {chainId: CHAIN_ID});
    if (response.data[0]) {
      return response.data[0];
    } else {
      return null;
    }
  },
  assetDetails: async (assetId: string): Promise<IAssetDetails | null> => {
    const url = `/assets/details/${assetId}`;
    const response: { data: IAssetDetails } = await makeNodeRequest(url, {chainId: CHAIN_ID});
    if (response.data) {
      return response.data;
    } else {
      return null;
    }
  },
  activeLeases: async (address: string, sender?: string): Promise<ILeaseInfo[]> => {
    const url = `/leasing/active/${address}`;
    const response: { data: ILeaseInfo[] } = await makeNodeRequest(url, {chainId: CHAIN_ID});
    if (response.data) {
      return sender !== undefined ? response.data.filter(x => x.sender === sender) : response.data;
    } else {
      return [];
    }
  },
  getGeneratingBalance: async (address: string): Promise<number> => {
    const wavesUrl = `/addresses/balance/details/${address}`;
    const response = await makeNodeRequest(wavesUrl, {chainId: CHAIN_ID});
    return response.data.generating;
  },
  getEffectiveBalance: async (address: string): Promise<number> => {
    const wavesUrl = `/addresses/balance/details/${address}`;
    const response = await makeNodeRequest(wavesUrl, {chainId: CHAIN_ID});
    return response.data.effective;
  },
};

export default nodeService;
